import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Row, Col, Spinner, Badge } from "react-bootstrap";
import DataTable from "react-data-table-component";
import RangePicker from "./RangePicker";
import {
  ButtonGroup,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
  Popover,
  PopoverBody,
  PopoverHeader,
  UncontrolledDropdown,
} from "reactstrap";
import { formatDate } from "../../helpers/dateFormat";
import ConfirmModal from "../ConfirmModal";
import { withTranslation } from "react-i18next";

const customTableStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #e9ecef",
      minHeight: "48px",
    },
  },
  headCells: {
    style: {
      fontWeight: "600",
      fontSize: "13px",
      color: "#495057",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "52px",
      fontSize: "14px",
      color: "#212529",
      "&:hover": {
        backgroundColor: "#f1f5f9",
        cursor: "pointer",
      },
    },
    stripedStyle: {
      backgroundColor: "#fafbfc",
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #e9ecef",
      minHeight: "56px",
    },
  },
};

const CustomTable = ({
  data,
  columns,
  onStatusChange,
  buttonLabel,
  buttonAction,
  buttonIcon = "bx bx-plus",
  tableTitle,
  showSearch = true,
  searchPlaceholder,
  searchKeys = [],
  showRangePicker = true,
  showAddButton = true,
  headerContent,
  onRowClicked,
  t,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [confirmModel, setConfirmModel] = useState({ isOpen: false });
  const togglePopover = () => setPopoverOpen(!popoverOpen);
  const [paginationState, setPaginationState] = useState({
    ...data?.pagination,
  });

  const isLoading = data?.loading;

  useEffect(() => {
    setPaginationState({ ...(data?.pagination || {}) });
  }, [data]);

  const handleTableChange = (props) => {
    setPaginationState({ ...paginationState, ...props });
    if (onStatusChange) {
      onStatusChange((prev) => ({ ...prev, ...props }));
    }
  };

  const paginationOptions = {
    paginationTotalRows: paginationState?.total || 0,
    paginationPerPage: paginationState?.limit || 10,
    paginationDefaultPage: paginationState?.currentPage || 1,
    sortServer: true,
    onSort: (column, order) =>
      handleTableChange({ sort: order, sortField: column.sortField, page: 1 }),
    onChangePage: (page) => handleTableChange({ page }),
    onChangeRowsPerPage: (limit) => handleTableChange({ limit, page: 1 }),
  };

  const handleActionOnClick = (item, data) => {
    if (!item?.onClick) return;
    if (item?.isConfirm) {
      const toggle = () => setConfirmModel({ isOpen: false });
      setConfirmModel({
        isOpen: true,
        onConfirm: () => {
          item?.onClick(data);
          toggle();
        },
        toggle,
        ...item,
      });
    } else {
      item?.onClick(data);
    }
  };

  const enhancedColumns = useMemo(() => {
    return columns.map((col) => {
      const enhanced = { ...col };

      if (enhanced?.isStatus) {
        enhanced.selector = (data) => (
          <Badge
            bg={data.isActive == 1 ? "success" : "danger"}
            className="text-uppercase px-2 py-1"
            style={{ cursor: "pointer", fontSize: "11px" }}
            onClick={() =>
              enhanced?.onClick &&
              enhanced?.onClick({
                id: data.id,
                isActive: data.isActive == 0 ? "1" : "0",
              })
            }
          >
            {data.isActive == 1
              ? t("customTable.filter.active")
              : t("customTable.filter.inactive")}
          </Badge>
        );
      }

      if (enhanced?.dateFormat) {
        enhanced.selector = (row) =>
          formatDate(row?.[enhanced?.dateField], enhanced.dateFormat) ?? "—";
      }

      if (enhanced?.dropdownItems?.length) {
        enhanced.cell = (data) => (
          <UncontrolledDropdown className="dropdown d-inline-block">
            <DropdownToggle
              className="btn btn-soft-secondary btn-sm rounded-circle"
              tag="button"
              style={{ width: "32px", height: "32px", padding: 0 }}
            >
              <i className="ri-more-2-fill align-middle"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end shadow-sm">
              {enhanced?.dropdownItems.map((item, index) => (
                <DropdownItem
                  key={index}
                  href={item.href}
                  className={`${item.className || ""} py-2`}
                  onClick={() => handleActionOnClick(item, data)}
                >
                  <i
                    className={`${item.icon} align-bottom me-2 text-muted`}
                  ></i>
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      }

      if (enhanced?.actionItems?.length) {
        enhanced.cell = (data) => (
          <ButtonGroup size="sm">
            {enhanced?.actionItems.map((item, index) => (
              <button
                type="button"
                key={index}
                className={`${item.className} btn-sm`}
                title={item.label}
                onClick={() => handleActionOnClick(item, data)}
                style={{ padding: "4px 10px" }}
              >
                <i className={`${item.icon} align-bottom`}></i>
              </button>
            ))}
          </ButtonGroup>
        );
      }

      return {
        ...enhanced,
        name: (
          <span className="font-weight-bold fs-13">{enhanced.name}</span>
        ),
        filter: undefined,
      };
    });
  }, [columns, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    const searchData = {};
    searchKeys.forEach((key) => {
      searchData[key] = searchText;
    });
    onStatusChange((prev) => ({
      ...prev,
      ...searchData,
    }));
  };

  const handleClearSearch = () => {
    setSearchText("");
    const searchData = {};
    searchKeys.forEach((key) => {
      searchData[key] = "";
    });
    onStatusChange((prev) => ({
      ...prev,
      ...searchData,
    }));
  };

  const totalRecords = paginationState?.total || data?.data?.length || 0;

  const NoDataComponent = () => (
    <div className="text-center py-5">
      <i
        className="bx bx-folder-open text-muted"
        style={{ fontSize: "48px" }}
      ></i>
      <p className="text-muted mt-2 mb-0">{t("customTable.noData")}</p>
    </div>
  );

  const LoadingComponent = () => (
    <div className="text-center py-5">
      <Spinner animation="border" variant="primary" size="sm" />
      <p className="text-muted mt-2 mb-0">{t("customTable.loading")}</p>
    </div>
  );

  return (
    <div>
      <Card className="shadow-sm border-0">
        <Card.Header
          className="bg-white border-bottom"
          style={{ padding: "16px 20px" }}
        >
          {/* Row 1: Title/Button + Search/Filter */}
          <Row className="align-items-center g-3">
            <Col xs={12} md="auto" className="d-flex align-items-center gap-3">
              {tableTitle && (
                <h5 className="mb-0 fw-semibold text-dark">{tableTitle}</h5>
              )}
              {showAddButton && (
                <Button
                  variant="primary"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={buttonAction}
                >
                  <i className={buttonIcon}></i>
                  {buttonLabel || t("customTable.addButton")}
                </Button>
              )}
              {totalRecords > 0 && (
                <Badge bg="light" text="dark" className="fw-normal px-2 py-1" style={{ fontSize: "12px" }}>
                  {totalRecords} {totalRecords == 1 ? t("customTable.record") : t("customTable.records")}
                </Badge>
              )}
            </Col>

            {showSearch && (
              <Col xs={12} md className="d-flex justify-content-md-end gap-2">
                <InputGroup style={{ maxWidth: "320px" }}>
                  <Input
                    type="text"
                    value={searchText}
                    placeholder={
                      searchPlaceholder || t("customTable.searchPlaceholder")
                    }
                    className="border-end-0"
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                  {searchText && (
                    <InputGroupText
                      style={{
                        cursor: "pointer",
                        backgroundColor: "white",
                        borderLeft: "none",
                        borderRight: "none",
                      }}
                      onClick={handleClearSearch}
                    >
                      <i className="bx bx-x text-muted"></i>
                    </InputGroupText>
                  )}
                  <InputGroupText
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={handleSearch}
                  >
                    <i className="bx bx-search-alt text-muted"></i>
                  </InputGroupText>
                </InputGroup>

                {showRangePicker && (
                  <>
                    <Button
                      id="filterPopoverBtn"
                      variant="outline-secondary"
                      size="sm"
                      className="d-flex align-items-center gap-1"
                      onClick={togglePopover}
                    >
                      <i className="bx bx-filter-alt"></i>
                    </Button>
                    <Popover
                      placement="bottom-end"
                      className="filter-popover"
                      isOpen={popoverOpen}
                      target="filterPopoverBtn"
                      toggle={togglePopover}
                      offset={[0, 4]}
                    >
                      <PopoverHeader className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">
                            {t("customTable.popover.dateRange")}
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => {
                              handleTableChange({
                                startDate: "",
                                endDate: "",
                                rangeStartStepCompleted: false,
                                rangeEndStepCompleted: false,
                                dateRangeSelectionStatus: "",
                              });
                              togglePopover();
                            }}
                          >
                            {t("customTable.popover.reset")}
                          </Button>
                        </div>
                      </PopoverHeader>
                      <PopoverBody className="p-0">
                        <RangePicker
                          isPopoverOpen={popoverOpen}
                          onStatusChange={onStatusChange}
                          onRangeSelectionStep={({ step }) => {
                            if (step === "end") {
                              setPopoverOpen(false);
                            }
                          }}
                        />
                      </PopoverBody>
                    </Popover>
                  </>
                )}
              </Col>
            )}
          </Row>

          {/* Optional: Extra header content (e.g., filter buttons) */}
          {headerContent && (
            <div className="mt-3 pt-3 border-top">{headerContent}</div>
          )}
        </Card.Header>

        <Card.Body className="p-0">
          <DataTable
            columns={enhancedColumns}
            data={data?.data || []}
            pagination
            paginationServer
            striped
            highlightOnHover
            pointerOnHover={!!onRowClicked}
            onRowClicked={onRowClicked}
            responsive
            persistTableHead
            customStyles={customTableStyles}
            noDataComponent={<NoDataComponent />}
            progressPending={isLoading}
            progressComponent={<LoadingComponent />}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            {...paginationOptions}
          />
        </Card.Body>
      </Card>
      <ConfirmModal {...confirmModel} />
    </div>
  );
};

export default withTranslation()(CustomTable);
